import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const items = [
  {
    type: 'work',
    role: 'Frontend Developer',
    company: 'Abhyam Robotics Institution of Business & Technology',
    period: 'Nov 2022 — Aug 2023',
    accent: '#3b82f6',
    bullets: [
      'Built and maintained multiple client-facing web apps including an e-commerce platform, POS system, school management system, and pharmacy management system',
      'Developed responsive, accessible UI components using React and Tailwind CSS',
      'Worked directly from Figma designs, translating them into pixel-perfect interfaces',
      'Integrated REST APIs and collaborated with backend developers for smooth data flow',
    ],
    tech: ['React', 'Tailwind CSS', 'JavaScript', 'HTML', 'Figma', 'REST APIs'],
  },
  {
    type: 'education',
    role: 'Bachelor of Information Technology (BIT)',
    company: 'Kent Institute Australia',
    period: '2024 — Present',
    accent: '#8b5cf6',
    bullets: [
      'Core subjects: Data Structures & Algorithms, Web Development, Software Engineering',
      'Building strong foundations in full-stack development and computer science principles',
      'Applying classroom theory directly to real-world projects and internship applications',
    ],
    tech: [],
  },
];

function TimelineItem({ item, index }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, x: -32 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      style={{ display: 'flex', gap: '2rem', marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${item.accent}`, background: `${item.accent}20`, boxShadow: `0 0 14px ${item.accent}55`, flexShrink: 0, marginTop: '0.35rem' }} />
        {index < items.length - 1 && (
          <div style={{ width: 1, flex: 1, background: `linear-gradient(to bottom, ${item.accent}25, transparent)`, marginTop: '0.5rem', minHeight: 80 }} />
        )}
      </div>

      <div className="glass-card" style={{ flex: 1, padding: '1.75rem', marginBottom: '2rem' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#f0f6ff', marginBottom: '0.2rem' }}>{item.role}</p>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: item.accent }}>{item.company}</p>
          </div>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', color: '#475569', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '6px', padding: '0.25rem 0.7rem', whiteSpace: 'nowrap' }}>
            {item.period}
          </span>
        </div>

        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: item.tech.length ? '1.25rem' : 0 }}>
          {item.bullets.map((b, i) => (
            <li key={i} style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
              <span style={{ color: item.accent, fontSize: '0.65rem', marginTop: '0.28rem', flexShrink: 0 }}>▹</span>
              <span style={{ color: '#64748b', fontSize: '0.86rem', lineHeight: 1.65 }}>{b}</span>
            </li>
          ))}
        </ul>

        {item.tech.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {item.tech.map(t => (
              <span key={t} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.64rem', color: '#a5b4fc', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '5px', padding: '0.18rem 0.55rem' }}>{t}</span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function Experience() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section id="experience" style={{ padding: '120px 0' }}>
      <div className="section-container">
        <motion.p ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className="section-label">
          Experience
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.1 }} className="section-title" style={{ marginBottom: '1rem' }}>
          Where I've{' '}
          <span style={{ background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Built Things
          </span>
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4rem', maxWidth: '480px', lineHeight: 1.7 }}>
          Professional experience shipping real products, plus my current B.IT journey.
        </motion.p>
        <div style={{ maxWidth: 750 }}>
          {items.map((item, i) => <TimelineItem key={i} item={item} index={i} />)}
        </div>
      </div>
    </section>
  );
}
